/**
 * C端 API 基础请求封装
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
}

export interface PaginatedResponse<T> {
  list: T[]
  total: number
  page: number
  pageSize: number
}

export class ApiError extends Error {
  constructor(
    message: string,
    public code: number,
    public statusCode?: number
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const getToken = (): string | null => {
  return localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token')
}

export const setToken = (token: string, remember?: boolean): void => {
  if (remember) {
    localStorage.setItem('customer_token', token)
  } else {
    sessionStorage.setItem('customer_token', token)
  }
}

export const clearToken = (): void => {
  localStorage.removeItem('customer_token')
  sessionStorage.removeItem('customer_token')
}

const instance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

instance.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

instance.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { data } = response
    // 兼容两种响应包装：v2.2 NestJS {success, data} / v1.0 {code, data}
    const isV2Ok = typeof (data as any)?.success === 'boolean' && (data as any).success === true
    const isV1Ok = data.code === 200 || data.code === 0
    if (!isV2Ok && !isV1Ok) {
      const statusCode = (data as any)?.code ?? 500
      if (statusCode === 401 || statusCode === 403) {
        clearToken()
        window.dispatchEvent(new CustomEvent('auth:expired'))
      }
      throw new ApiError(data.message || '请求失败', statusCode, response.status)
    }
    return (data as any)?.data ?? data.data ?? data
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response
      if (status === 401 || status === 403) {
        clearToken()
        window.dispatchEvent(new CustomEvent('auth:expired'))
      }
      throw new ApiError(data?.message || '请求失败', data?.code || status, status)
    }
    throw new ApiError('网络连接失败，请检查网络', -1)
  }
)

export const api = {
  get: <T = any>(url: string, params?: Record<string, any>, config?: AxiosRequestConfig): Promise<T> =>
    instance.get(url, { params, ...config }),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    instance.post(url, data, config),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    instance.put(url, data, config),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    instance.patch(url, data, config),

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    instance.delete(url, config),
}

export default instance
